import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { FaShoppingCart, FaHeart } from "react-icons/fa";
import Navbar from "../Components/Navbar";
import useProducts from "../hooks/useProducts";

const Marketplace = () => {
  const { products, loading } = useProducts();

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);

  const categories = [...new Set(products?.map(p => p.category))];

  useEffect(() => {
    if (!products) return;

    const filtered = products.filter(p =>
      (selectedCategory === "" || p.category === selectedCategory) &&
      p.product_name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    setFilteredProducts(filtered);
  }, [products, searchQuery, selectedCategory]);

  const handleAddToCart = (product, e) => {
    e.preventDefault();
    e.stopPropagation();

    setCart(prev => {
      const existing = prev.find(item => item.id === product.id);
      if (existing) {
        return prev.map(item =>
          item.id === product.id
            ? { ...item, qty: item.qty + 1 }
            : item
        );
      }
      return [...prev, { ...product, qty: 1 }];
    });
  };

  const totalItems = cart.reduce((sum, item) => sum + item.qty, 0);

  return (
    <Container>
      <Navbar activeSection="marketplace" />

      {/* FLOATING CART BUTTON */}
      <FloatingCart onClick={() => setShowCart(!showCart)}>
        <FaShoppingCart />
        {totalItems > 0 && <CartBadge>{totalItems}</CartBadge>}
      </FloatingCart>

      {/* CART DRAWER */}
      {showCart && (
        <CartDrawer>
          <h3>My Cart</h3>
          {cart.length === 0 ? (
            <p>No items yet</p>
          ) : (
            cart.map(item => (
              <CartItem key={item.id}>
                <span>{item.product_name}</span>
                <span>x{item.qty}</span>
              </CartItem>
            ))
          )}
        </CartDrawer>
      )}

      {/* HERO */}
      <HeroSection>
        <HeroOverlay>
          <HeroContent>
            <SmallText>Welcome to Ina-Ani</SmallText>
            <HeroTitle>
              Nurturing Harvests,<br />
              <span>Empowering Women</span>
            </HeroTitle>
            <HeroDesc>
              Discover fresh, locally-sourced produce and traditional Filipino delicacies.
            </HeroDesc>
          </HeroContent>
        </HeroOverlay>
      </HeroSection>

      {/* FIXED SEARCH */}
      <StickySearch>
        <SearchInput
          placeholder="Search for Tablia, Lettuce, etc."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />

        <CategorySelect
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
        >
          <option value="">All Categories</option>
          {categories.map((c, i) => (
            <option key={i}>{c}</option>
          ))}
        </CategorySelect>

        <SearchButton>Search</SearchButton>
      </StickySearch>

      {/* SCROLLABLE PRODUCTS */}
      <ScrollableSection>
        {loading ? (
          <Loading>Loading products...</Loading>
        ) : (
          <Grid>
            {filteredProducts.length > 0 ? (
              filteredProducts.map((p) => (
                <Card key={p.id}>

                  <Link to={`/products/${p.id}`} style={{ textDecoration: "none" }}>
                    <ImageWrap>
                      <img
                        src={`data:image/jpeg;base64,${p.image}`}
                        alt={p.product_name}
                      />
                    </ImageWrap>

                    <Content>
                      <h3>{p.product_name}</h3>
                      <Price>₱{p.price}.00</Price>
                      <Desc>{p.description}</Desc>
                    </Content>
                  </Link>

                  <Actions>
                    <CartBtn onClick={(e) => handleAddToCart(p, e)}>
                      <FaShoppingCart /> Add
                    </CartBtn>

                    <Heart>
                      <FaHeart />
                    </Heart>
                  </Actions>

                </Card>
              ))
            ) : (
              <NoData>No products found</NoData>
            )}
          </Grid>
        )}
      </ScrollableSection>
    </Container>
  );
};

export default Marketplace;

/* ================= STYLES ================= */

const Container = styled.div`
  background: #f5f7f2;
  min-height: 100vh;
  font-family: Arial;
`;

/* FLOATING CART */
const FloatingCart = styled.div`
  position: fixed;
  right: 20px;
  bottom: 20px;
  background: #2e7d32;
  color: white;
  padding: 15px;
  border-radius: 50%;
  cursor: pointer;
  z-index: 999;
`;

const CartBadge = styled.div`
  position: absolute;
  top: -5px;
  right: -5px;
  background: red;
  color: white;
  font-size: 12px;
  padding: 3px 6px;
  border-radius: 50%;
`;

const CartDrawer = styled.div`
  position: fixed;
  right: 0;
  top: 0;
  width: 300px;
  height: 100%;
  background: white;
  box-shadow: -2px 0 10px rgba(0,0,0,0.2);
  padding: 20px;
  z-index: 998;
`;

const CartItem = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 10px 0;
`;

/* HERO */
const HeroSection = styled.div`
  height: 280px;
  margin: 20px;
  border-radius: 20px;
  background: url("https://images.unsplash.com/photo-1542838132-92c53300491e") center/cover no-repeat;
  overflow: hidden;
`;

const HeroOverlay = styled.div`
  width: 100%;
  height: 100%;
  padding: 30px;
  background: linear-gradient(to right, rgba(0,80,30,0.85), rgba(0,0,0,0.2));
`;

const HeroContent = styled.div`
  color: white;
  max-width: 600px;
`;

const SmallText = styled.p`
  font-size: 0.9rem;
  opacity: 0.8;
`;

const HeroTitle = styled.h1`
  font-size: 2.3rem;
  margin: 10px 0;

  span {
    color: #81c784;
  }
`;

const HeroDesc = styled.p`
  font-size: 1rem;
`;

/* FIXED SEARCH */
const StickySearch = styled.div`
  position: sticky;
  top: 0;
  z-index: 500;
  margin: -40px auto 20px;
  background: white;
  padding: 12px;
  border-radius: 50px;
  display: flex;
  gap: 10px;
  max-width: 900px;
  box-shadow: 0 4px 10px rgba(0,0,0,0.1);
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 10px;
  border: none;
  outline: none;
`;

const CategorySelect = styled.select`
  padding: 10px;
  border-radius: 10px;
  border: 1px solid #ccc;
`;

const SearchButton = styled.button`
  background: #2e7d32;
  color: white;
  border: none;
  padding: 10px 18px;
  border-radius: 30px;
  cursor: pointer;
`;

/* SCROLLABLE PRODUCTS */
const ScrollableSection = styled.div`
  height: calc(100vh - 380px);
  overflow-y: auto;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 25px;
  padding: 20px;
`;

const Card = styled.div`
  background: white;
  border-radius: 15px;
  overflow: hidden;
  transition: 0.3s;
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);

  &:hover {
    transform: translateY(-5px);
  }
`;

const ImageWrap = styled.div`
  height: 180px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.4s ease;
  }

  &:hover img {
    transform: scale(1.1);
  }
`;

const Content = styled.div`
  padding: 15px;

  h3 {
    margin: 0;
    color: #1b5e20;
  }
`;

const Price = styled.p`
  color: #2e7d32;
  font-weight: bold;
`;

const Desc = styled.p`
  font-size: 0.85rem;
  color: #666;
`;

const Actions = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0 15px 15px;
`;

const CartBtn = styled.button`
  background: #2e7d32;
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 20px;
  display: flex;
  gap: 5px;
  cursor: pointer;
`;

const Heart = styled.div`
  background: #e8f5e9;
  padding: 8px;
  border-radius: 50%;
  cursor: pointer;
  color: #2e7d32;
`;

const Loading = styled.p`
  text-align: center;
  padding: 40px;
`;

const NoData = styled.p`
  text-align: center;
  grid-column: 1 / -1;
`;
